import { useState } from 'react';

/**
 * Hook customizado para gerenciar upload e processamento de imagens
 * @returns {Object} Funções e estados para gerenciar imagens
 */
const useImageUpload = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [originalImagePath, setOriginalImagePath] = useState(null);
  
  // Configurar imagem com caminho existente
  const setExistingImage = (imagePath, getImageUrl) => {
    if (imagePath) {
      setOriginalImagePath(imagePath);
      const url = getImageUrl(imagePath);
      setPreviewImage(url);
    } else {
      resetImage();
    }
  };

  // Limpar imagem
  const resetImage = () => {
    setOriginalImagePath(null);
    setPreviewImage(null);
    setSelectedImage(null);
    setCropperOpen(false);
  };

  // Manipular seleção de imagem
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result);
        setCropperOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Manipular conclusão do recorte da imagem
  const handleCropComplete = (croppedImage, setFormData = null) => {
    // Definir a URL de preview
    setPreviewImage(croppedImage.url);
    setCropperOpen(false);
    
    // Se uma função setFormData for fornecida, atualize o formData com o blob da imagem
    if (setFormData) {
      setFormData(prevData => ({
        ...prevData,
        imagem_perfil: croppedImage.blob
      }));
    }
  };

  // Lidar com erro de carregamento de imagem
  const handleImageError = (event, customTheme) => {
    try {
      const imgSrc = event.target.src;
      console.log('Erro ao carregar imagem:', imgSrc);
      
      // Se a imagem já é a padrão ou já tentamos todas as alternativas, parar
      if (imgSrc.startsWith('data:image/svg+xml') || imgSrc.includes('_fallback')) {
        return;
      }
      
      // Obter base URLs
      const { API_URL } = require('../config/api');
      const fullBaseUrl = API_URL;
      
      // Extrair o nome do arquivo
      const imgParts = imgSrc.split('/');
      const filename = imgParts[imgParts.length - 1];
      
      // Flag para indicar que esta é uma tentativa alternativa
      const hasAttemptedFix = imgSrc.includes('_attempt');
      
      if (hasAttemptedFix) {
        // Se já tentamos uma correção e falhou, usar imagem padrão
        console.log('Tentativa de correção falhou, usando imagem padrão');
        const { getDefaultImage } = require('../utils/formatters');
        event.target.src = getDefaultImage();
        return;
      }
      
      // Tentar URL alternativa baseada no endpoint direto
      if (imgSrc.includes('/usuarios/imagem/')) {
        // Tentar URL direta de uploads
        console.log('Tentando URL direta de uploads');
        event.target.src = `${fullBaseUrl}/uploads/profile-images/${filename}?_attempt=1`;
      } else if (imgSrc.includes('/uploads/profile-images/')) {
        // Tentar endpoint específico
        console.log('Tentando endpoint específico');
        event.target.src = `${fullBaseUrl}/usuarios/imagem/${filename}?_attempt=1`;
      } else {
        // Nenhuma das correções funcionou, usar imagem padrão
        console.log('Nenhuma URL alternativa disponível, usando imagem padrão');
        const { getDefaultImage } = require('../utils/formatters');
        event.target.src = getDefaultImage();
      }
    } catch (error) {
      console.error('Erro ao tentar corrigir URL da imagem:', error);
      try {
        // Usar imagem padrão como fallback
        const { getDefaultImage } = require('../utils/formatters');
        event.target.src = `${getDefaultImage()}?_fallback=true`;
      } catch (secondError) {
        // Se tudo falhar, esconder a imagem
        event.target.style.display = 'none';
      }
    }
  };

  // Preparar FormData com imagem
  const prepareImageFormData = (formData, formDataToSend) => {
    if (formData.imagem_perfil && formData.imagem_perfil instanceof Blob) {
      // Adiciona a extensão .jpg ao arquivo
      const blob = formData.imagem_perfil;
      const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
      formDataToSend.append('imagem_perfil', file);
    } else if (originalImagePath) {
      // Se estamos editando e não há nova imagem selecionada, precisamos
      // passar o caminho original para o backend saber que não muda a imagem
      formDataToSend.append('original_image_path', originalImagePath);
    }
  };

  return {
    selectedImage,
    previewImage,
    cropperOpen,
    originalImagePath,
    setExistingImage,
    resetImage,
    handleImageSelect,
    handleCropComplete,
    handleImageError,
    prepareImageFormData,
    setCropperOpen
  };
};

export default useImageUpload; 